import './BuildingInfoFormModal.scss';
import { AreaUnit, BuildingType, CountryCode } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AreaUnitView } from 'src/modules/common/components/AreaUnitView';
import { Button } from 'src/modules/common/components/Button';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { BUILDING_TYPE_OPTIONS, UNIT_VERSION_OPTIONS } from 'src/modules/common/constants/options';
import { CARBON_THEME } from 'src/modules/common/constants/theme';
import { useCountryList } from 'src/modules/common/hooks/useCountryList';
import { ArrowLeftIcon } from 'src/modules/common/icons/ArrowLeftIcon';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { AutocompleteOption } from 'src/modules/common/types/AutocompleteOption';
import { DecimalField } from 'src/modules/form/components/DecimalField';
import { DropdownField } from 'src/modules/form/components/DropdownField';
import { SearchableSelectField } from 'src/modules/form/components/SearchableSelectField';
import { TextField } from 'src/modules/form/components/TextField';
import { useFormValidator } from 'src/modules/form/hooks/useFormValidator';
import { FormError } from 'src/modules/form/types/FormError';
import { ensureNonNull } from 'src/modules/form/utils/transform';
import { BUILDING_SCHEMA } from 'src/modules/project-new/constants/schema';
import { BuildingFormData } from 'src/modules/project-new/types/BuildingFormData';

type Props = {
  readonly formData: BuildingFormData;
  readonly onSubmit: (form: FormController<BuildingFormData, FormError>) => void;
  readonly onBack: (form: FormController<BuildingFormData, FormError>) => void;
  readonly onClose: () => void;
};

export const BuildingInfoFormModal = ({ formData, onSubmit, onBack, onClose }: Props): React.ReactElement => {
  const intl = useIntl();
  const countryCodes = useCountryList();

  const form = useForm<BuildingFormData, FormError>('project.create.new.buidling', {
    reinitialize: false,
    initialValues: formData,
  });
  useFormValidator(form, BUILDING_SCHEMA);

  const handleSubmit = useCallback((): void => {
    onSubmit(form);
  }, [form, onSubmit]);
  const handleBack = useCallback((): void => {
    onBack(form);
  }, [form, onBack]);

  const isSubmitting = useFormWatch(form, form.submitting.get);

  return (
    <>
      <ModalHeader
        label={intl.formatMessage({ id: 'project/create/building/title' })}
        title={intl.formatMessage({ id: 'project/create/building/subTitle' })}
        handleClose={onClose}
      />

      <ModalBody>
        <div className="bp-project-create-modal__body">
          <div className="bp-building-info-form-modal__row bp-building-info-form-modal__row--grid">
            <DropdownField<BuildingType, BuildingFormData>
              size="lg"
              options={BUILDING_TYPE_OPTIONS}
              field={form.field.at('type')}
              label={(
                <>
                  {intl.formatMessage({ id: 'basedata/building/type' })}
                  <span style={{ color: CARBON_THEME.belimoOrangeColor }}>*</span>
                </>
              )}
              renderOption={renderBuildingTypeOption}
            />

            <DecimalField
              size="lg"
              theme="gray"
              field={form.field.at('size')}
              label={intl.formatMessage({ id: 'basedata/building/size' })}
              autoComplete="off"
            />

            <DropdownField
              size="lg"
              options={UNIT_VERSION_OPTIONS}
              field={form.field.at('unit').transform(ensureNonNull())}
              label={intl.formatMessage({ id: 'basedata/building/unit' })}
              renderOption={renderUnitOptions}
            />
          </div>

          <div className="bp-building-info-form-modal__row">
            <TextField
              size="lg"
              field={form.field.at('address').at('street')}
              label={intl.formatMessage({ id: 'basedata/building/address/street' })}
              autoComplete="off"
              maxLength={30}
              description={null}
            />
          </div>

          <div className="bp-building-info-form-modal__row">
            <TextField
              size="lg"
              field={form.field.at('address').at('city')}
              label={intl.formatMessage({ id: 'basedata/building/address/city' })}
              autoComplete="off"
              maxLength={20}
              description={null}
            />

            <TextField
              size="lg"
              field={form.field.at('address').at('zip')}
              label={intl.formatMessage({ id: 'basedata/building/address/zipCode' })}
              autoComplete="off"
              maxLength={10}
              description={null}
            />
          </div>

          <div className="bp-building-info-form-modal__row">
            <TextField
              size="lg"
              field={form.field.at('address').at('state')}
              label={intl.formatMessage({ id: 'basedata/building/address/state' })}
              autoComplete="off"
              maxLength={40}
              description={null}
            />

            <SearchableSelectField
              size="lg"
              label={(
                <>
                  <FormattedMessage id="basedata/building/address/country"/>
                  <span style={{ color: CARBON_THEME.belimoOrangeColor }}>*</span>
                </>
              )}
              field={form.field.at('address').at('country')}
              options={countryCodes}
              formatOptionLabel={(option) => intl.formatMessage({ id: `common/country/name/${option.value}` })}
              menuPlacement="top"
            />
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          className="bp-project-create-modal__button"
          kind="secondary"
          fullWidth={true}
          onClick={handleBack}
          reversed={true}
        >
          <FormattedMessage id="project/create/action/back"/>

          <div className="bp-project-create-modal__button-icon">
            <ArrowLeftIcon/>
          </div>
        </Button>

        <Button
          disabled={isSubmitting}
          kind="primary"
          onClick={handleSubmit}
          autoFocus={true}
          fullWidth={true}
          loading={isSubmitting}
          icon={(
            <div className="bp-project-create-modal__button-icon">
              <ArrowRightIcon/>
            </div>
          )}
          data-test-id="create-project-button"
        >
          <FormattedMessage id="project/create/action/create"/>
        </Button>
      </ModalFooter>
    </>
  );
};

function renderBuildingTypeOption(option: BuildingType): React.ReactElement {
  return <FormattedMessage id={`common/buildingType/${option}`}/>;
}

function renderUnitOptions(option: AreaUnit): React.ReactElement {
  return <AreaUnitView unit={option}/>;
}

function renderCountryOption(option: AutocompleteOption<CountryCode | null>): React.ReactNode {
  return option ? <FormattedMessage id={`common/country/name/${option.value}`}/> : null;
}
